import { FC, useContext, useEffect, useReducer, useState } from "react";
import SubscriptionContext from "./SubscriptionContext";
import SubscriptionReducer from "./SubscriptionReducer";
import {
  CreateSubscription,
  GetUserSubscriptions,
  CancelSubscription,
} from "./SubscriptionFunction";
import WalletContext from "../Wallet/WalletContext";
import AlertContext from "../Alert/AlertContext";
import { Subscription } from "../../interfaces";
import { Contracts } from "../../config/contracts";
import { useAccount } from "wagmi";
import { BaseError, Hash, CallExecutionError, TransactionExecutionError, WaitForTransactionReceiptTimeoutError, UserRejectedRequestError } from "viem";
import { useWeb3 } from "../../services/web3/hooks/useWeb3";
import AuthContext from "../Auth/AuthContext";
import { waitForTransactionReceipt } from "../Wallet/WalletFunction";
import { LowGasError } from "../../services/web3";
import { appConfig } from "../../config";

const initialState = {
  subscriptions: [],
  pendingSubscriptions: [],
  totalTickets: 0,
  lockedGains: 0,
  lockedGainsPad: 0,
  unlockedGains: 0,
  awaitingLock: false,
  step: "NONE",
  userTier: null,
};

const SubscriptionState: FC = (props) => {

  const { isAuth } = useContext<any>(AuthContext);

  const [state, dispatch] = useReducer(SubscriptionReducer, initialState);
  const [waitModal, setWaitModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const { refetchGains, chainId }  = useWeb3();

  const {
    networkId,
    lockToken,
    verifyLockToken,
    approveTokenSpent,
    fetchTokenAllowance,
  } = useContext<any>(WalletContext);
  const { address, chain } = useAccount();
  const { setAlert } = useContext<any>(AlertContext);
  const { getProfile, getProfileData } = useContext<any>(AuthContext);

  useEffect(() => {
    if (isAuth) getUserSubscriptions();
  }, [ isAuth ])

  useEffect(() => {
    if (getProfileData && getProfileData.total_staked_across_platforms) {
      dispatch({
        type: "SET_TOTAL_LOCKED_GAINS",
        payload: getProfileData.total_staked_across_platforms,
      });
    }
  }, [getProfileData])

  const getUserSubscriptions = () => {
    GetUserSubscriptions()
      .then((data: any) => {
        dispatch({
          type: "GET_SUBSCRIPTIONS",
          payload: data.getUserSubscriptions,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const createSubscription = (totalToken: number, duration: number) => {

    setWaitModal(true);

    dispatch({
      type: "UPDATE_SUBSCRIPTION_STEP",
      payload: "APPROVAL_FETCH",
    });

    // let tokenAddress: string | undefined, spender: string | undefined;

    if (!chainId) {
      setAlert("No chain information found, please reconnect wallet to continue", "error");
      return;
    }

    const scVersion = parseInt(appConfig.latestLockContractVersion.toString(), 10);
    const tokenAddress = chain?.id ? Contracts.gains[chain?.id] : "";
    const lockAddresses = Contracts.lockFactory[scVersion.toString()] as Record<string, `0x${string}`>;
    const spender = lockAddresses[chainId.toString()];

    fetchTokenAllowance(chain?.id, tokenAddress, spender, address)
    .then((allowance: any) => {
        if (allowance < totalToken) {
          dispatch({
            type: "UPDATE_SUBSCRIPTION_STEP",
            payload: "AWAITING_ALLOWANCE",
          });
          approveTokenSpent(chain?.id, totalToken, tokenAddress, spender)
            .then((transactionHash: any) => {
              dispatch({
                type: "UPDATE_SUBSCRIPTION_STEP",
                payload: "APPROVED",
              });
              setAlert("Successfully approved GAINS spending.", "success");
              lockAndVerify(
                totalToken,
                duration,
                tokenAddress,
                networkId,
                address
              );
            })
            .catch((error: any) => {
              dispatch({
                type: "UPDATE_SUBSCRIPTION_STEP",
                payload: "NONE",
              });
              setWaitModal(false);
              if (error instanceof TransactionExecutionError) {
                setAlert("User rejected token spend approve request. Please accept to continue with staking.", "error");
              } else if (error instanceof LowGasError) {
                setAlert("Insufficient balance to pay for gas. Please top up your account and try again.", "error");
              } else {
                setAlert("Unable to approve token spending. Please try again later.", "error");
              }
            });
        } else {
          dispatch({
            type: "UPDATE_SUBSCRIPTION_STEP",
            payload: "APPROVED",
          });

          setAlert("Token was already approved, proceeding with lock.", "info");
          lockAndVerify(
            totalToken,
            duration,
            tokenAddress,
            networkId,
            address
          );
        }
      }
    )
    .catch((error: any) => {
      dispatch({
        type: "UPDATE_SUBSCRIPTION_STEP",
        payload: "NONE",
      });
      setWaitModal(false);
      setAlert('Unable to fetch token approval', "error");
    })
  };

  const lockAndVerify = (
    totalToken: any,
    duration: any,
    tokenAddress: any,
    networkId: any,
    address: any
  ) => {
    CreateSubscription(totalToken, duration, address, networkId)
      .then((data: any) => {
        if (data.createSubscription) {
          dispatch({
            type: "NEW_SUBSCRIPTION",
            payload: data.createSubscription,
          });

          let { totalToken, duration, id } = data.createSubscription;

          lockToken(totalToken, duration, id, tokenAddress)
            .then((transactionHash: any) => {
              dispatch({
                type: "UPDATE_SUBSCRIPTION_STEP",
                payload: "LOCKED",
              });

              setAlert("Successfully locked token on chain.", "success");

              dispatch({
                type: "UPDATE_SUBSCRIPTION_STEP",
                payload: "VERIFICATION",
              });

              verifyLockTokenTransaction(id, transactionHash, address);
            })
            .catch((error: any) => {
              setWaitModal(false);
              dispatch({
                type: "UPDATE_SUBSCRIPTION_STEP",
                payload: "NONE",
              });
              
              if (error instanceof LowGasError) {
                setAlert("Insufficient balance to pay for gas. Please top up your account and try again.", "error", 20000);
                CancelSubscription(id, 'UserRejectedRequestError')
                  .then((data: any) => {
                    dispatch({
                      type: "CANCEL_PENDING_SUBSCRIPTION",
                      payload: id,
                    });
                  })
                  .catch((error: any) => {
                    console.log(error);
                  });
              } else {
                const isUserRejectedRequestError = error.walk((e: any) => e instanceof UserRejectedRequestError) instanceof UserRejectedRequestError
                if (isUserRejectedRequestError) {
                  setAlert(`Please approve the transaction on your wallet to stake GAINS.`, "error");
                  CancelSubscription(id, 'UserRejectedRequestError')
                    .then((data: any) => {
                      dispatch({
                        type: "CANCEL_PENDING_SUBSCRIPTION",
                        payload: id,
                      });
                    })
                    .catch((error: any) => {
                      console.log(error);
                    });
                } else if (error instanceof WaitForTransactionReceiptTimeoutError) {
                  setAlert('Transaction confirmation delayed. Please allow us some time to check for confirmation.', "info", 10000);
                  // Transaction should not be cancelled, as it may still be pending
                  // Watch for transaction receipt and verify on success               
                  // Get transaction hash from the message 
                  waitForConfirmation(id, address, error);
                } else {
                  setAlert(
                    `We ran into an issue receiving transaction verification. Please allow us some time to check for confirmation.`,
                    "warning",
                    10000
                  );
                }
              }
            });
        }
      })
      .catch((error: any) => {
        setWaitModal(false);
        setAlert(
          "Error with creating subscription, please try again.",
          "error"
        );
      });
  };

  const verifyLockTokenTransaction = (id: string, transactionHash: string, address: string) => {
    verifyLockToken(id, transactionHash, address)
      .then((subscription: any) => {
        dispatch({
          type: "UPDATE_SUBSCRIPTION_STEP",
          payload: "LOCK_VERIFIED",
        });
        dispatch({
          type: "UPDATE_SUBSCRIPTIONS",
          payload: subscription,
        });
        setWaitModal(false);
        refetchGains();
        getProfile(); // Should return the latest tier if there are any changes
        dispatch({
          type: "UPDATE_SUBSCRIPTION_STEP",
          payload: "NONE",
        });
        setAlert(
          `Successfully locked ${subscription.totalToken} GAINS, transaction verfied.`, "success"
        )
        setSuccessModal(true);
      })
      .catch((error: any) => {
        setAlert(
          "Transaction was successful, please give us some time to verify the transaction.",
          "warning"
        );
        setWaitModal(false);
        dispatch({
          type: "UPDATE_SUBSCRIPTION_STEP",
          payload: "NONE",
        });
        dispatch({
          type: "CANCEL_PENDING_SUBSCRIPTION",
          payload: id,
        });
      });
  }

  const waitForConfirmation = (id: string, address: string, error: WaitForTransactionReceiptTimeoutError) => {
    waitForTransactionReceipt(error)
      .then((hash: Hash) => {
        dispatch({
          type: "UPDATE_SUBSCRIPTION_STEP",
          payload: "LOCKED",
        });

        setAlert("Successfully locked token on chain.", "success");

        dispatch({
          type: "UPDATE_SUBSCRIPTION_STEP",
          payload: "VERIFICATION",
        });
        verifyLockTokenTransaction(id, hash, address);
      })
      .catch((error) => {
        setAlert(
          "Lock token transaction failed, please try again",
          "error"
        );
        dispatch({
          type: "UPDATE_SUBSCRIPTION_STEP",
          payload: "NONE",
        });
        dispatch({
          type: "CANCEL_PENDING_SUBSCRIPTION",
          payload: id,
        });
      })
  }

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptions: state.subscriptions,
        pendingSubscriptions: state.pendingSubscriptions,
        totalTickets: state.totalTickets,
        lockedGains: state.lockedGains,
        lockedGainsPad: state.lockedGainsPad,
        unlockedGains: state.unlockedGains,
        awaitingLock: state.awaitingLock,
        waitModal: waitModal,
        step: state.step,
        successModal: successModal,
        setSuccessModal,
        setWaitModal,
        getUserSubscriptions,
        createSubscription,
      }}
    >
      {props.children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionState;
